import {
  AfterViewInit,
  ChangeDetectorRef,
  Directive,
  ElementRef,
  Input,
  NgModule,
  Renderer2,
  SimpleChanges,
  ViewChild,
  ViewContainerRef,
  OnChanges,
} from '@angular/core';
import { IconComponent, SystemIconComponentModule } from '../icon/icon.component';

/**
 * Directive to dynamically insert an icon component inside an element.
 * The icon can be positioned to the left or right of the element's content.
 * @group Directives
 */
@Directive({
  selector: '[systemIconDirective]',
})
export class IconDirective implements AfterViewInit, OnChanges {
  @ViewChild('iconContainer', {
    read: ViewContainerRef,
    static: true,
  })
  iconContainer!: ViewContainerRef;

  /**
   * The name of the icon to be displayed.
   * This input is required.
   * @group Props
   */
  @Input({ required: true }) iconName = '';
  /**
   * The size of the icon in pixels.
   * Default value is 24.
   * @group Props
   */
  @Input() iconSize = 24;
  /**
   * The type of the icon, which can be either 'solid' or 'outline'.
   * Default value is 'solid'.
   * @group Props
   */
  @Input() iconType: 'solid' | 'outline' = 'solid';
  /**
   * The position of the icon relative to the label.
   * It can be either 'left' or 'right'.
   * Default value is 'right'.
   * @group Props
   */
  @Input() iconPosition: 'left' | 'right' = 'right';

  private iconComponent: any;
  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private viewContainerRef: ViewContainerRef,
    private cdr: ChangeDetectorRef
  ) {}

  ngAfterViewInit() {
    this.createIconComponent();
    this.insertIcon();
    this.setStyles();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.iconComponent) return;
    this.updateIcon(changes);
  }

  private createIconComponent() {
    this.validateInputs();
    // Create the icon component dynamically
    const factory = this.viewContainerRef.createComponent(IconComponent);
    this.iconComponent = factory;
    this.updateIcon();
  }

  private insertIcon() {
    // Insert the icon based on the iconPosition
    const originalContent = this.htmlElement;
    if (this.iconPosition === 'left') {
      this.renderer.insertBefore(
        originalContent,
        this.iconComponent.location.nativeElement,
        originalContent.firstChild
      );
    } else {
      this.renderer.appendChild(originalContent, this.iconComponent.location.nativeElement);
    }
  }

  private setStyles() {
    // Set the styles
    const originalContent = this.htmlElement;
    this.renderer.setStyle(originalContent, 'display', 'inline-flex');
    this.renderer.setStyle(originalContent, 'align-items', 'center');
    this.cdr.detectChanges();
  }

  private updateIcon(changes?: SimpleChanges) {
    this.validateInputs();
    this.iconComponent.instance.iconName = this.iconName;
    this.iconComponent.instance.iconSize = this.iconSize;
    this.iconComponent.instance.iconType = this.iconType;
    if (changes) {
      if (changes.iconPosition) {
        this.insertIcon();
      }
    }
  }

  private validateInputs() {
    if (!this.iconName) {
      throw new Error('Icon name is required');
    }
  }

  private get htmlElement(): HTMLElement {
    return this.elementRef.nativeElement;
  }
}

@NgModule({
  declarations: [IconDirective],
  imports: [SystemIconComponentModule],
  exports: [IconDirective],
})
export class SystemIconDirectiveModule {}
